import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { ScoreBreakdown, ScoreDrilldownDialog } from '../components/features/Cards';
import { DiligenceAgentsPanel } from '../components/features/DiligenceAgentsPanel';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { startupApi, investorApi, watchlistApi } from '../lib/api';
import {
  cn,
  formatConfidencePercent,
  formatCurrency,
  formatDate,
  formatDateWithPrecision,
  formatRoundLabel,
  getStageColor,
  getScoreColor,
  readSessionJson,
  writeSessionJson,
} from '../lib/utils';
import { 
  ArrowLeft, Loader2, Sparkles, FileText, Users, TrendingUp, 
  Briefcase, Building2, Globe, Github, Linkedin, Twitter, ExternalLink,
  Star, Zap, Target, AlertCircle, CheckCircle, Plus, ShieldCheck
} from 'lucide-react';

const buildStartupDetailCacheKey = (startupId) => `agent-vc.startup-detail.v3:${startupId}`;

const StartupDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startup, setStartup] = useState(null);
  const [investorFits, setInvestorFits] = useState([]);
  const [generatingMemo, setGeneratingMemo] = useState(false);
  const [generatingScores, setGeneratingScores] = useState(false);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [scoreDrilldown, setScoreDrilldown] = useState(null);
  const [scoreDrilldownLoading, setScoreDrilldownLoading] = useState(false);
  const [scoreDrilldownError, setScoreDrilldownError] = useState(null);
  const [activeTab, setActiveTab] = useState('ai-panel');
  const autoEnrichedRef = useRef(false);

  const fetchStartup = async () => {
    const cacheKey = buildStartupDetailCacheKey(id);
    const cachedStartup = readSessionJson(cacheKey, null);

    try {
      const response = await startupApi.get(id);
      setStartup(response.data);
      writeSessionJson(cacheKey, response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch startup:', err);
      if (cachedStartup) {
        setStartup(cachedStartup);
        setError('Live detail data is still warming up. Showing the freshest cached company profile available.');
      } else {
        setError('Could not load the startup profile right now.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestorFit = async () => {
    try {
      const response = await investorApi.findFit(id, 10);
      setInvestorFits(response.data.fits || []);
    } catch (err) {
      console.error('Failed to fetch investor fits:', err);
    }
  };

  const checkWatchlist = async () => {
    try {
      const response = await watchlistApi.check(id);
      setInWatchlist(response.data.in_watchlist);
    } catch (err) {
      console.error('Failed to check watchlist:', err);
    }
  };

  useEffect(() => {
    autoEnrichedRef.current = false;
    fetchStartup();
    fetchInvestorFit();
    checkWatchlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!startup || autoEnrichedRef.current) {
      return;
    }

    if (!startup.website || startup.latest_deck) {
      autoEnrichedRef.current = true;
      return;
    }

    autoEnrichedRef.current = true;
    (async () => {
      try {
        const response = await startupApi.enrich(id);
        setStartup(response.data);
      } catch (err) {
        console.error('Failed to enrich startup:', err);
      }
    })();
  }, [startup, id]);

  const handleGenerateMemo = async () => {
    setGeneratingMemo(true);
    try {
      const response = await startupApi.generateMemo(id);
      setStartup({ ...startup, memo: response.data.memo });
    } catch (err) {
      console.error('Failed to generate memo:', err);
    } finally {
      setGeneratingMemo(false);
    }
  };

  const handleGenerateScores = async () => {
    setGeneratingScores(true);
    try {
      const response = await startupApi.runDiligence(id);
      setStartup((current) => ({
        ...current,
        conviction_score: response.data,
        score_drilldown_available: true,
      }));
      setError(null);
    } catch (err) {
      console.error('Failed to generate scores:', err);
    } finally {
      setGeneratingScores(false);
    }
  };

  const openScoreDrilldown = async () => {
    if (!startup) {
      return;
    }

    setScoreDialogOpen(true);
    setScoreDrilldown(null);
    setScoreDrilldownError(null);
    setScoreDrilldownLoading(true);

    try {
      const response = await startupApi.getDiligenceReport(startup.startup_id);
      setScoreDrilldown(response.data);
    } catch (err) {
      console.error('Failed to fetch score drilldown:', err);
      setScoreDrilldownError('The persisted diligence trace could not be loaded.');
    } finally {
      setScoreDrilldownLoading(false);
    }
  };

  const handleAddToWatchlist = async () => {
    setWatchlistLoading(true);
    try {
      if (inWatchlist) {
        await watchlistApi.remove(id);
        setInWatchlist(false);
      } else {
        await watchlistApi.add(id);
        setInWatchlist(true);
      }
    } catch (err) {
      console.error('Failed to toggle watchlist:', err);
    } finally {
      setWatchlistLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
        </div>
      </MainLayout>
    );
  }

  if (!startup) {
    return (
      <MainLayout>
        <div className="p-6 text-center">
          <p className="text-zinc-400">Startup not found</p>
          <Button onClick={() => navigate('/discover')} variant="outline" className="mt-4">
            Back to Discover
          </Button>
        </div>
      </MainLayout>
    );
  }

  const convictionScore = startup.conviction_score?.total_score || 0;
  const latestDeckUrl = startup.latest_deck?.url || null;
  const latestDeckTitle = startup.latest_deck?.title || startup.latest_deck?.deck_title || 'Latest deck';
  const foundersCount = startup.founders_count ?? startup.founders?.length ?? 0;
  const locationLabel = [startup.city, startup.state, startup.country].filter(Boolean).join(', ');
  const latestFundingRound = startup.latest_funding_round || null;
  const latestFundingDate = latestFundingRound?.date
    ? formatDateWithPrecision(latestFundingRound.date, latestFundingRound.date_precision)
    : null;
  const trustSummary = startup.trust_summary || null;
  const researchTeam = Array.isArray(startup.startup_people)
    ? startup.startup_people.filter((person) => person.role_type !== 'founder')
    : [];

  return (
    <MainLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/discover')}
            className="text-zinc-400 hover:text-zinc-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-3 rounded-md border border-amber-400/20 bg-amber-400/10 px-4 py-3">
            <AlertCircle className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-100">{error}</p>
          </div>
        )}

        {/* Main Info */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6 mb-6">
          <div className="flex items-start gap-6">
            {/* Logo */}
            <div className="w-20 h-20 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
              {startup.logo_url ? (
                <img src={startup.logo_url} alt={startup.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-zinc-500 font-mono text-3xl">{startup.name?.charAt(0)}</span>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-medium font-['Outfit'] text-zinc-50">{startup.name}</h1>
                {convictionScore > 0 && (
                  <button
                    type="button"
                    onClick={openScoreDrilldown}
                    className={cn("flex items-center gap-1 px-3 py-1 rounded-full font-mono transition-colors hover:bg-zinc-800",
                      convictionScore >= 80 ? 'bg-emerald-400/10 text-emerald-400' :
                      convictionScore >= 60 ? 'bg-blue-400/10 text-blue-400' :
                      convictionScore >= 40 ? 'bg-amber-400/10 text-amber-400' : 'bg-zinc-800 text-zinc-400'
                    )}
                    data-testid="startup-score-open"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span className="text-lg">{Math.round(convictionScore)}</span>
                  </button>
                )}
              </div>

              <p className="text-zinc-400 font-['Manrope'] mb-4 max-w-2xl">{startup.description}</p>

              {/* Tags */}
              <div className="flex items-center gap-2 flex-wrap mb-4">
                {startup.stage && (
                  <span className={cn("px-3 py-1 text-sm font-mono rounded-full border", getStageColor(startup.stage))}>
                    {startup.stage}
                  </span>
                )}
                {startup.sector && (
                  <span className="px-3 py-1 text-sm font-mono text-zinc-400 bg-zinc-800 rounded-full">
                    {startup.sector}
                  </span>
                )}
                {startup.source_vc && (
                  <span className="px-3 py-1 text-sm font-mono text-blue-400 bg-blue-400/10 rounded-full border border-blue-400/20">
                    <Building2 className="w-3.5 h-3.5 inline mr-1" />
                    {startup.source_vc}
                  </span>
                )}
                {latestFundingRound?.round_type && (
                  <span className="px-3 py-1 text-sm font-mono text-emerald-300 bg-emerald-400/10 rounded-full border border-emerald-400/20">
                    {formatRoundLabel(latestFundingRound.round_type)}
                    {latestFundingDate ? ` • ${latestFundingDate}` : ''}
                  </span>
                )}
                {trustSummary?.confidence_percent && (
                  <span className="px-3 py-1 text-sm font-mono text-zinc-300 bg-zinc-800 rounded-full border border-zinc-700">
                    <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />
                    Trust {trustSummary.confidence_percent}%
                  </span>
                )}
                {startup.city && (
                  <span className="px-3 py-1 text-sm font-mono text-zinc-400 bg-zinc-800/70 rounded-full">
                    {startup.city}
                  </span>
                )}
                {startup.country && (
                  <span className="px-3 py-1 text-sm font-mono text-zinc-500 bg-zinc-800/50 rounded-full">
                    {startup.country}
                  </span>
                )}
              </div>

              {trustSummary?.signals?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {trustSummary.signals.map((signal) => (
                    <span
                      key={signal.key || signal.label}
                      className="px-2.5 py-1 rounded-full text-xs font-mono text-zinc-400 bg-zinc-800/80 border border-zinc-700"
                    >
                      {signal.label}
                    </span>
                  ))}
                </div>
              )}

              {/* Links */}
              <div className="flex items-center gap-3">
                {startup.website && (
                  <a href={startup.website} target="_blank" rel="noopener noreferrer" 
                     className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-50 text-sm">
                    <Globe className="w-4 h-4" />
                    Website
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {startup.team_page_url && (
                  <a href={startup.team_page_url} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-50 text-sm">
                    <Users className="w-4 h-4" />
                    Team
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {startup.careers_url && (
                  <a href={startup.careers_url} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-50 text-sm">
                    <Briefcase className="w-4 h-4" />
                    Careers
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {startup.github_url && (
                  <a href={startup.github_url} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-50 text-sm">
                    <Github className="w-4 h-4" />
                    GitHub
                  </a>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => setActiveTab('ai-panel')}
                data-testid="run-diligence-btn"
                className="bg-white text-zinc-900 hover:bg-zinc-200"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Run Diligence
              </Button>
              <Button
                onClick={handleAddToWatchlist}
                variant="outline"
                disabled={watchlistLoading}
                data-testid="add-watchlist-btn"
                className={cn(
                  "border-zinc-700 hover:text-zinc-50",
                  inWatchlist ? "text-amber-400 border-amber-400/30 hover:border-amber-400/50" : "text-zinc-400"
                )}
              >
                {watchlistLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Star className={cn("w-4 h-4 mr-2", inWatchlist && "fill-amber-400")} />
                )}
                {inWatchlist ? 'In Watchlist' : 'Add to Watchlist'}
              </Button>
              <Button
                onClick={() => navigate(`/deal-rooms?startup=${startup.name}`)}
                variant="outline"
                className="border-zinc-700 text-zinc-400 hover:text-zinc-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Deal Room
              </Button>
            </div>
          </div>
        </div>

        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="ai-panel" className="data-[state=active]:bg-zinc-800 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              AI Panel
            </TabsTrigger>
            <TabsTrigger value="overview" className="data-[state=active]:bg-zinc-800">Overview</TabsTrigger>
            <TabsTrigger value="scores" className="data-[state=active]:bg-zinc-800">Diligence</TabsTrigger>
            <TabsTrigger value="signals" className="data-[state=active]:bg-zinc-800">Signals</TabsTrigger>
            <TabsTrigger value="investors" className="data-[state=active]:bg-zinc-800">Investor Fit</TabsTrigger>
            <TabsTrigger value="memo" className="data-[state=active]:bg-zinc-800">Investment Memo</TabsTrigger>
          </TabsList>

          {/* AI Diligence Panel Tab */}
          <TabsContent value="ai-panel">
            <DiligenceAgentsPanel
              startupId={startup.startup_id}
              startupName={startup.name}
            />
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-3 gap-6">
              {/* Quick Stats */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
                <h3 className="text-sm font-medium text-zinc-400 mb-4 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Key Metrics
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-zinc-500 text-xs font-mono mb-1">Total Funding</p>
                    <p className="text-zinc-50 text-lg font-mono">{formatCurrency(startup.total_funding)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs font-mono mb-1">Latest Round</p>
                    <p className="text-zinc-50 text-lg font-mono">
                      {latestFundingRound?.round_type ? formatRoundLabel(latestFundingRound.round_type) : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs font-mono mb-1">Funding Date</p>
                    <p className="text-zinc-50 text-lg font-mono">{latestFundingDate || '-'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs font-mono mb-1">Team Size</p>
                    <p className="text-zinc-50 text-lg font-mono">{startup.team_size || '-'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs font-mono mb-1">Founders</p>
                    <p className="text-zinc-50 text-lg font-mono">{foundersCount || '-'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs font-mono mb-1">Founded</p>
                    <p className="text-zinc-50 text-lg font-mono">{startup.founded_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs font-mono mb-1">Location</p>
                    <p className="text-zinc-50 text-lg font-mono">{locationLabel || '-'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs font-mono mb-1">Research Confidence</p>
                    <p className="text-zinc-50 text-lg font-mono">
                      {trustSummary?.confidence ? formatConfidencePercent(trustSummary.confidence) : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Score Summary */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
                <h3 className="text-sm font-medium text-zinc-400 mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Score Summary
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">Investor Respect</span>
                    <span className={cn("font-mono", getScoreColor(startup.investor_respect_score || 0))}>
                      {Math.round(startup.investor_respect_score || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">Team Confidence</span>
                    <span className={cn("font-mono", getScoreColor(startup.team_confidence_score || 0))}>
                      {Math.round(startup.team_confidence_score || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">Sector Momentum</span>
                    <span className={cn("font-mono", getScoreColor(startup.sector_momentum_score || 0))}>
                      {Math.round(startup.sector_momentum_score || 0)}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    onClick={openScoreDrilldown}
                    disabled={!startup.score_drilldown_available && !startup.conviction_score}
                    className="w-full border-zinc-700 text-zinc-300 hover:text-zinc-50"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Open Diligence Trace
                  </Button>
                </div>
              </div>

              {/* Backed By */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
                <h3 className="text-sm font-medium text-zinc-400 mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Backed By
                </h3>
                <div className="space-y-2">
                  {startup.source_vcs?.length > 0 ? (
                    startup.source_vcs.map((vc, i) => (
                      <div key={i} className="flex items-center gap-2 text-zinc-300">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        {vc}
                      </div>
                    ))
                  ) : startup.source_vc ? (
                    <div className="flex items-center gap-2 text-zinc-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      {startup.source_vc}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-sm">No VC data available</p>
                  )}
                </div>
              </div>
            </div>

            {/* Founders */}
            {(startup.founders?.length > 0 || researchTeam.length > 0) && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
                <h3 className="text-sm font-medium text-zinc-400 mb-4 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Founders & Team Evidence
                </h3>
                {startup.founders?.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {startup.founders.map((founder, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-md" data-testid={`founder-card-${i}`}>
                        <div className="w-10 h-10 bg-zinc-700 rounded-full flex items-center justify-center text-zinc-400 text-sm font-mono flex-shrink-0">
                          {founder.name?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-zinc-50 font-medium">{founder.name}</p>
                            {founder.linkedin_url && (
                              <a href={founder.linkedin_url} target="_blank" rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`founder-linkedin-${i}`}
                                className="text-blue-400/60 hover:text-blue-400 transition-colors"
                              >
                                <Linkedin className="w-3.5 h-3.5" />
                              </a>
                            )}
                            {founder.trust?.confidence && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono text-zinc-300 bg-zinc-800 border border-zinc-700">
                                {formatConfidencePercent(founder.trust.confidence)}
                              </span>
                            )}
                            {founder.source_count > 0 && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono text-zinc-500 bg-zinc-900 border border-zinc-800">
                                {founder.source_count} source{founder.source_count === 1 ? '' : 's'}
                              </span>
                            )}
                          </div>
                          {founder.title && (
                            <p className="text-zinc-400 text-sm">{founder.title}</p>
                          )}
                          {founder.background && (
                            <p className="text-zinc-500 text-xs mt-1 line-clamp-2">{founder.background}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {researchTeam.length > 0 && (
                  <div className={cn("grid gap-3 mt-4", startup.founders?.length > 0 && "pt-4 border-t border-zinc-800")}>
                    <p className="text-zinc-500 text-xs font-mono">Additional research-backed people</p>
                    <div className="grid grid-cols-2 gap-3">
                      {researchTeam.slice(0, 4).map((person) => (
                        <div key={person.person_id || person.name} className="p-3 bg-zinc-800/40 rounded-md border border-zinc-800">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-zinc-100 text-sm font-medium">{person.name}</p>
                            {person.linkedin_url && (
                              <a
                                href={person.linkedin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400/60 hover:text-blue-400 transition-colors"
                              >
                                <Linkedin className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                          {person.title && (
                            <p className="text-zinc-400 text-xs mt-1">{person.title}</p>
                          )}
                          {person.bio && (
                            <p className="text-zinc-500 text-xs mt-1 line-clamp-2">{person.bio}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Funding Rounds */}
            {(startup.funding_rounds?.length > 0 || latestFundingRound) && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
                <h3 className="text-sm font-medium text-zinc-400 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Funding History
                </h3>
                <div className="space-y-3">
                  {(startup.funding_rounds?.length > 0 ? startup.funding_rounds : [latestFundingRound]).map((round, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-md" data-testid={`funding-round-${i}`}>
                      <div>
                        <p className="text-zinc-50 font-medium">{formatRoundLabel(round.round_type)}</p>
                        {round.lead_investor && (
                          <p className="text-zinc-500 text-xs">Led by {round.lead_investor}</p>
                        )}
                        {round.date && (
                          <p className="text-zinc-600 text-xs font-mono mt-1">
                            {formatDateWithPrecision(round.date, round.date_precision)}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        {round.amount && (
                          <p className="text-zinc-50 font-mono">{formatCurrency(round.amount)}</p>
                        )}
                        {round.investors?.length > 0 && (
                          <p className="text-zinc-500 text-xs">{round.investors.slice(0, 3).join(', ')}</p>
                        )}
                        {round.confidence && (
                          <p className="text-zinc-600 text-xs font-mono mt-1">
                            {formatConfidencePercent(round.confidence)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Website & Links */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
              <h3 className="text-sm font-medium text-zinc-400 mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Links & Resources
              </h3>
              <div className="flex flex-wrap gap-3">
                {startup.website && (
                  <a href={startup.website} target="_blank" rel="noopener noreferrer"
                    data-testid="startup-website-link"
                    className="flex items-center gap-2 px-3 py-2 bg-zinc-800 rounded-md text-blue-400 text-sm hover:bg-zinc-700 transition-colors"
                  >
                    <Globe className="w-4 h-4" /> {startup.website.replace('https://', '')} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {startup.team_page_url && (
                  <a href={startup.team_page_url} target="_blank" rel="noopener noreferrer"
                    data-testid="startup-team-page-link"
                    className="flex items-center gap-2 px-3 py-2 bg-zinc-800 rounded-md text-zinc-300 text-sm hover:bg-zinc-700 transition-colors"
                  >
                    <Users className="w-4 h-4" /> Team Page <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {startup.careers_url && (
                  <a href={startup.careers_url} target="_blank" rel="noopener noreferrer"
                    data-testid="startup-careers-link"
                    className="flex items-center gap-2 px-3 py-2 bg-zinc-800 rounded-md text-emerald-400 text-sm hover:bg-zinc-700 transition-colors"
                  >
                    <Briefcase className="w-4 h-4" /> Careers <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {startup.github_url && (
                  <a href={startup.github_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-zinc-800 rounded-md text-zinc-300 text-sm hover:bg-zinc-700 transition-colors"
                  >
                    <Github className="w-4 h-4" /> GitHub <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {latestDeckUrl && (
                  <a href={latestDeckUrl} target="_blank" rel="noopener noreferrer"
                    data-testid="startup-latest-deck-link"
                    className="flex items-center gap-2 px-3 py-2 bg-zinc-800 rounded-md text-amber-400 text-sm hover:bg-zinc-700 transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    Latest Deck
                    <span className="text-zinc-500">{latestDeckTitle}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Scores Tab */}
          <TabsContent value="scores">
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium font-['Outfit'] text-zinc-50">Multi-Agent Diligence Breakdown</h3>
                <div className="flex items-center gap-3">
                  <Button
                    onClick={openScoreDrilldown}
                    variant="outline"
                    disabled={!startup.score_drilldown_available && !startup.conviction_score}
                    className="border-zinc-700 text-zinc-300 hover:text-zinc-50"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Open Diligence Trace
                  </Button>
                  <Button
                    onClick={handleGenerateScores}
                    disabled={generatingScores}
                    data-testid="run-diligence-tab-btn"
                    className="bg-white text-zinc-900 hover:bg-zinc-200"
                  >
                    {generatingScores ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    {generatingScores ? 'Running Diligence...' : 'Run Diligence'}
                  </Button>
                </div>
              </div>
              
              {startup.conviction_score ? (
                <div className="space-y-6">
                  {startup.conviction_score?.explanations?.summary && (
                    <div className="grid gap-4 md:grid-cols-[minmax(0,2fr),minmax(280px,1fr)]">
                      <div className="bg-zinc-800/40 border border-zinc-800 rounded-md p-4">
                        <p className="text-zinc-500 text-xs font-mono mb-2">Ranking Summary</p>
                        <p className="text-zinc-300 text-sm leading-relaxed">
                          {startup.conviction_score.explanations.summary}
                        </p>
                      </div>
                      <div className="bg-zinc-800/40 border border-zinc-800 rounded-md p-4">
                        <p className="text-zinc-500 text-xs font-mono mb-2">Stored Confidence</p>
                        <p className="text-zinc-50 text-lg font-mono">
                          {formatConfidencePercent(startup.conviction_score.explanations.confidence) || '-'}
                        </p>
                        {startup.conviction_score.explanations.analysis_run_id && (
                          <p className="text-zinc-600 text-xs font-mono mt-2">
                            {startup.conviction_score.explanations.analysis_run_id}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <ScoreBreakdown scores={startup.conviction_score} />
                </div>
              ) : (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-400 mb-4">No diligence run yet</p>
                  <p className="text-zinc-500 text-sm">Click "Run Diligence" to launch the multi-agent analysis.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Signals Tab */}
          <TabsContent value="signals">
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
              <h3 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-6">Signal Timeline</h3>
              
              {startup.signals?.length > 0 ? (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[19px] top-0 bottom-0 w-px bg-zinc-800" />
                  
                  <div className="space-y-0">
                    {startup.signals.map((signal, i) => {
                      const getSignalStyle = (type) => {
                        switch (type) {
                          case 'funding':
                            return { bg: 'bg-emerald-400', ring: 'ring-emerald-400/20', text: 'text-emerald-400', badge: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20' };
                          case 'hiring':
                            return { bg: 'bg-blue-400', ring: 'ring-blue-400/20', text: 'text-blue-400', badge: 'bg-blue-400/10 text-blue-400 border-blue-400/20' };
                          case 'product_launch':
                            return { bg: 'bg-amber-400', ring: 'ring-amber-400/20', text: 'text-amber-400', badge: 'bg-amber-400/10 text-amber-400 border-amber-400/20' };
                          case 'news':
                            return { bg: 'bg-rose-400', ring: 'ring-rose-400/20', text: 'text-rose-400', badge: 'bg-rose-400/10 text-rose-400 border-rose-400/20' };
                          case 'technical':
                            return { bg: 'bg-violet-400', ring: 'ring-violet-400/20', text: 'text-violet-400', badge: 'bg-violet-400/10 text-violet-400 border-violet-400/20' };
                          default:
                            return { bg: 'bg-zinc-400', ring: 'ring-zinc-400/20', text: 'text-zinc-400', badge: 'bg-zinc-400/10 text-zinc-400 border-zinc-400/20' };
                        }
                      };
                      const style = getSignalStyle(signal.signal_type);
                      const isLast = i === startup.signals.length - 1;
                      
                      return (
                        <div key={signal.signal_id || i} className="relative flex gap-4 pb-6" data-testid={`signal-item-${i}`}>
                          {/* Timeline dot */}
                          <div className="relative z-10 flex-shrink-0">
                            <div className={cn("w-[10px] h-[10px] rounded-full mt-1.5 ring-4", style.bg, style.ring)} />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0 pb-2">
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                              <span className={cn("px-2 py-0.5 text-[10px] font-mono rounded-full border uppercase tracking-wider", style.badge)}>
                                {signal.signal_type?.replace('_', ' ')}
                              </span>
                              {signal.reliability_score > 0 && (
                                <span className="text-zinc-600 text-[10px] font-mono">
                                  {Math.round(signal.reliability_score * 100)}% reliable
                                </span>
                              )}
                              <span className="text-zinc-600 text-[10px] font-mono ml-auto">
                                {signal.timestamp ? new Date(signal.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                              </span>
                            </div>
                            
                            <p className="text-zinc-200 text-sm font-['Manrope'] font-medium mb-1">{signal.title}</p>
                            
                            {signal.description && signal.description !== signal.title && (
                              <p className="text-zinc-500 text-sm font-['Manrope']">{signal.description}</p>
                            )}
                            
                            {signal.source && (
                              <p className="text-zinc-600 text-xs font-mono mt-1.5">
                                via {signal.source}
                                {signal.source_url && (
                                  <a href={signal.source_url} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-400/60 hover:text-blue-400">
                                    [link]
                                  </a>
                                )}
                              </p>
                            )}

                            {signal.impact_score > 0 && (
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-zinc-600 text-[10px] font-mono">Impact:</span>
                                <div className="flex gap-0.5">
                                  {[1, 2, 3, 4, 5].map((level) => (
                                    <div
                                      key={level}
                                      className={cn("w-3 h-1.5 rounded-full",
                                        level <= Math.ceil(signal.impact_score * 5) ? style.bg : 'bg-zinc-800'
                                      )}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <TrendingUp className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-400">No signals tracked yet</p>
                  <p className="text-zinc-500 text-sm mt-1">Signals appear when funding, hiring, or product events are detected</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Investor Fit Tab */}
          <TabsContent value="investors">
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
              <h3 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-4">Best Investor Matches</h3>
              
              {investorFits.length > 0 ? (
                <div className="space-y-3">
                  {investorFits.map((fit, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-md">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-zinc-700 rounded-full flex items-center justify-center text-zinc-400 text-sm font-mono">
                          {fit.investor_name?.charAt(0)}
                        </div>
                        <div>
                          <p className="text-zinc-50 font-medium">{fit.investor_name}</p>
                          <p className="text-zinc-400 text-sm">{fit.firm} • {fit.investor_type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn("font-mono text-lg", getScoreColor(fit.fit_score * 100))}>
                          {Math.round(fit.fit_score * 100)}%
                        </p>
                        <p className="text-zinc-500 text-xs">match</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-400">No investor matches found</p>
                  <p className="text-zinc-500 text-sm mt-2">Add investors to see matches</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Memo Tab */}
          <TabsContent value="memo">
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium font-['Outfit'] text-zinc-50">Investment Memo</h3>
                <Button
                  onClick={handleGenerateMemo}
                  disabled={generatingMemo}
                  data-testid="generate-memo-btn"
                  className="bg-white text-zinc-900 hover:bg-zinc-200"
                >
                  {generatingMemo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                  {generatingMemo ? 'Generating...' : 'Generate AI Memo'}
                </Button>
              </div>
              
              {startup.memo ? (
                <div className="prose prose-invert prose-zinc max-w-none">
                  <pre className="whitespace-pre-wrap font-['Manrope'] text-zinc-300 text-sm leading-relaxed bg-transparent p-0">
                    {startup.memo}
                  </pre>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-400 mb-4">No memo generated yet</p>
                  <p className="text-zinc-500 text-sm">Click "Generate AI Memo" to create an investment memo</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <ScoreDrilldownDialog
          open={scoreDialogOpen}
          onOpenChange={setScoreDialogOpen}
          startupName={startup?.name}
          drilldown={scoreDrilldown}
          loading={scoreDrilldownLoading}
          error={scoreDrilldownError}
          onRetry={openScoreDrilldown}
        />
      </div>
    </MainLayout>
  );
};

export default StartupDetail;
